import { ChatMessage, ChatSession as ChatSessionType, SessionMetadata } from '../types/chat';
import { ChatMessage as ChatMessageClass } from './ChatMessage';

export class ChatSession implements ChatSessionType {
  id: string;
  userId: string;
  title?: string;
  messages: ChatMessage[];
  status: 'active' | 'completed' | 'archived' | 'deleted';
  createdAt: string;
  updatedAt: string;
  metadata?: SessionMetadata;
  messageCount?: number;
  // 🆕 NEW: Enhanced session fields
  sessionType?: 'foundry' | 'traditional';
  isProcessing?: boolean;
  isError?: boolean;
  agentId?: string;
  threadId?: string;

  constructor(data: Partial<ChatSessionType & { 
    status?: 'active' | 'completed' | 'archived' | 'deleted', 
    messageCount?: number,
    sessionType?: 'foundry' | 'traditional',
    isProcessing?: boolean,
    isError?: boolean,
    agentId?: string,
    threadId?: string
  }> = {}) {
    this.id = data.id || this.generateId();
    this.userId = data.userId || '';
    this.title = data.title;
    this.messages = data.messages || [];
    this.status = data.status || 'active';
    this.createdAt = data.createdAt || new Date().toISOString();
    this.updatedAt = data.updatedAt || new Date().toISOString();
    this.metadata = data.metadata;
    this.messageCount = data.messageCount || this.messages.length;
    // 🆕 NEW: Initialize enhanced fields
    this.sessionType = data.sessionType;
    this.isProcessing = data.isProcessing || false;
    this.isError = data.isError || false;
    this.agentId = data.agentId;
    this.threadId = data.threadId;
  }

  private generateId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Add a message to the session
   */
  addMessage(message: ChatMessage): void {
    this.messages.push(message);
    this.updatedAt = new Date().toISOString();
  }

  /**
   * Get the last message in the session
   */
  getLastMessage(): ChatMessage | null {
    return this.messages.length > 0 ? this.messages[this.messages.length - 1] : null;
  }

  /**
   * Get messages by role
   */
  getMessagesByRole(role: 'user' | 'assistant' | 'system'): ChatMessage[] {
    return this.messages.filter(msg => msg.role === role);
  }

  /**
   * Get user messages count
   */
  getUserMessageCount(): number {
    return this.getMessagesByRole('user').length;
  }

  /**
   * Get assistant messages count
   */
  getAssistantMessageCount(): number {
    return this.getMessagesByRole('assistant').length;
  }

  /**
   * Generate title from first user message
   */
  generateTitle(): string {
    const firstUserMessage = this.messages.find(msg => msg.role === 'user');
    if (firstUserMessage) {
      const content = firstUserMessage.content.trim();
      return content.length > 50 ? content.substring(0, 47) + '...' : content;
    }
    return `Chat ${new Date(this.createdAt).toLocaleDateString()}`;
  }

  /**
   * Update session title
   */
  updateTitle(title: string): void {
    this.title = title;
    this.updatedAt = new Date().toISOString();
  }

  /**
   * Mark session as completed
   */
  markAsCompleted(): void {
    this.status = 'completed';
    this.updatedAt = new Date().toISOString();
  }

  /**
   * Archive the session
   */
  archive(): void {
    this.status = 'archived';
    this.updatedAt = new Date().toISOString();
  }

  /**
   * Check if session is active
   */
  isActive(): boolean {
    return this.status === 'active';
  }

  /**
   * Get session duration in minutes
   */
  getDurationInMinutes(): number {
    const start = new Date(this.createdAt);
    const end = new Date(this.updatedAt);
    return Math.round((end.getTime() - start.getTime()) / (1000 * 60));
  }

  /**
   * Update metadata
   */
  updateMetadata(updates: Partial<SessionMetadata>): void {
    this.metadata = { ...this.metadata, ...updates };
    this.updatedAt = new Date().toISOString();
  }

  /**
   * 🆕 NEW: Update quick questions
   */
  updateQuickQuestions(questions: string[]): void {
    if (!this.metadata) {
      this.metadata = {};
    }
    this.metadata.quickQuestions = questions;
    this.updatedAt = new Date().toISOString();
  }

  /**
   * 🆕 NEW: Get quick questions
   */
  getQuickQuestions(): string[] {
    return this.metadata?.quickQuestions || [];
  }

  /**
   * 🆕 NEW: Clear quick questions
   */
  clearQuickQuestions(): void {
    if (this.metadata) {
      delete this.metadata.quickQuestions;
      this.updatedAt = new Date().toISOString();
    }
  }

  /**
   * 🆕 NEW: Update processing state
   */
  updateProcessingState(isProcessing: boolean, isError: boolean = false): void {
    this.isProcessing = isProcessing;
    this.isError = isError;
    this.updatedAt = new Date().toISOString();
  }

  /**
   * 🆕 NEW: Check if session has quick questions
   */
  hasQuickQuestions(): boolean {
    const questions = this.getQuickQuestions();
    return questions.length > 0;
  }

  /**
   * Create from API response
   */
  static fromApiResponse(data: any): ChatSession {
    const session = new ChatSession(data);
    // Convert message objects to ChatMessage instances
    session.messages = data.messages?.map((msg: any) => ChatMessageClass.fromApiResponse(msg)) || [];
    return session;
  }

  /**
   * Create new session for user
   */
  static createForUser(userId: string, metadata?: SessionMetadata): ChatSession {
    return new ChatSession({
      userId,
      metadata
    });
  }

  /**
   * Prepare for API request (convert to plain object)
   */
  toApiPayload(): Omit<ChatSessionType, 'messages'> & { messages: any[] } {
    return {
      id: this.id,
      userId: this.userId,
      title: this.title,
      messages: this.messages.map(msg => ({
        id: msg.id,
        sessionId: msg.sessionId,
        content: msg.content,
        role: msg.role,
        timestamp: msg.timestamp,
        metadata: msg.metadata,
        status: msg.status
      })),
      status: this.status,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      metadata: this.metadata
    };
  }
}
